/**
 * PortfolioMap — geographic view of the assets in a portfolio.
 *
 * Coordinates come from the /overview payload (`lat` / `lon` per asset row,
 * sourced from the aus_warehouse geo crosswalk: Geoscience Australia power
 * stations reconciled against the AEMO registry). Coverage is partial, so
 * assets without a point are never guessed at from a region centroid — they're
 * listed under the map as "location not on file" instead.
 *
 * Two behaviours worth knowing about:
 *
 *  1. Sites, not DUIDs. Many portfolios hold several units at one physical
 *     site (Limondale LIMOSF11 + LIMOSF21 share an identical coordinate), and
 *     markers at identical positions render exactly on top of each other —
 *     the lower pin becomes unclickable. So we group by coordinate and draw
 *     one marker per site, with every unit listed in its popup.
 *
 *  2. Zoom follows the selection. One asset selected (or a single-site
 *     portfolio) flies to that site at a footprint-appropriate zoom so the
 *     plant itself fills the frame; several sites fit their bounds instead.
 *
 * The default basemap is satellite imagery rather than a street map: at site
 * zoom you can see the actual panel rows, turbine pads and substation, which
 * is the point of putting a map on this page at all.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Typography, Card, Stack, Skeleton, Tooltip, Button, Chip,
} from '@mui/material';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const FUEL_COLOR = {
  Solar:   '#f59e0b',
  Wind:    '#3b82f6',
  Battery: '#8b5cf6',
  Hydro:   '#06b6d4',
  Gas:     '#ef4444',
  Coal:    '#374151',
  Other:   '#6b7280',
};
const fuelColor = (f) => FUEL_COLOR[f] || FUEL_COLOR.Other;

const SELECT_COLOR = '#D98A2B';   // brand amber — matches the network map
const AU_CENTER = [-28.5, 134.0]; // fallback view before we have any points

// Floor for the map pane. Leaflet derives its zoom from the pane size, so the
// pane must never be allowed to collapse — see the size guard in MapFocus.
const MIN_PANE_HEIGHT = 320;

// Basemaps. Satellite is the default because the whole value of this map is
// seeing the real plant; the street layer is there for locating a site
// relative to towns and roads. Both are {z}/{x}/{y} XYZ services usable with
// attribution.
const BASEMAPS = {
  satellite: {
    label: 'Satellite',
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution: 'Imagery &copy; Esri, Maxar, Earthstar Geographics',
    maxZoom: 18,
  },
  street: {
    label: 'Street',
    // CARTO basemaps come in light and dark variants — follow the app theme so
    // the card doesn't flash a white rectangle in dark mode.
    url: (mode) => `https://{s}.basemaps.cartocdn.com/${mode === 'dark' ? 'dark_all' : 'light_all'}/{z}/{x}/{y}{r}.png`,
    attribution: '&copy; OpenStreetMap &copy; CARTO',
    maxZoom: 19,
  },
};

// Rounded to ~5dp (≈1 m) so two units registered at the same site collapse to
// one key. Guards against float noise producing two markers at "the same"
// place.
const siteKey = (lat, lon) => `${lat.toFixed(5)},${lon.toFixed(5)}`;

/**
 * How far to zoom when showing a single site. Generation plants differ in
 * physical footprint by more than an order of magnitude for the same MW, so a
 * fixed zoom either crops a wind farm or leaves a battery as a dot:
 *   - wind    → turbines spread over kilometres; pull back
 *   - battery → a compact containerised block; push in
 *   - solar   → scales with MW (roughly 1–2 ha per MW)
 * Capped at 16 so we stay inside the imagery's native resolution.
 */
function siteZoom(units) {
  const cap = units.reduce((s, u) => s + (u.capacity_mw || 0), 0);
  const fuels = new Set(units.map((u) => u.fuel));
  if (fuels.has('Wind'))  return cap > 150 ? 12 : 13;
  if (fuels.has('Hydro')) return 14;
  if (fuels.size === 1 && fuels.has('Battery')) return 16;
  if (cap > 200) return 13;
  if (cap > 75)  return 14;
  return 15;
}

// Site pin — a fuel-coloured teardrop. Scaled a little by capacity so the
// bigger plants read as bigger without turning the map into a bubble chart.
// The selected site gets an amber ring drawn into the same icon (rather than a
// second overlay marker) so it can never drift out of sync with the pin.
function siteIcon(color, capacityMw, selected, unitCount) {
  const s = capacityMw > 150 ? 34 : capacityMw > 50 ? 30 : 26;
  const ring = selected
    ? `<circle cx="12" cy="12" r="11" fill="none" stroke="${SELECT_COLOR}" stroke-width="2.2"/>`
    : '';
  // A "×N" badge tells the user a pin covers several units, so a portfolio of
  // 8 DUIDs showing 6 pins doesn't look like 2 assets went missing.
  const badge = unitCount > 1
    ? `<circle cx="18.5" cy="5.5" r="5" fill="#111" fill-opacity="0.82"/>
       <text x="18.5" y="7.9" text-anchor="middle" font-size="7"
             font-family="system-ui,sans-serif" font-weight="700" fill="#fff">${unitCount}</text>`
    : '';
  return L.divIcon({
    className: 'lm-portfolio-pin',
    html: `<svg width="${s}" height="${s}" viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="8.5" fill="${color}" stroke="#fff" stroke-width="2"/>
      <path d="M13 6l-5 8h3.2l-1 4.5 5-8H12z" fill="#fff"/>
      ${ring}${badge}</svg>`,
    iconSize: [s, s],
    iconAnchor: [s / 2, s / 2],
  });
}

/**
 * Drives the viewport off the current selection. Lives inside <MapContainer>
 * so it can grab the map instance via useMap().
 *
 * The effect key is a signature of the points plus the selection, so the map
 * re-frames when the user switches asset or the period refetch returns new
 * geo — but does NOT fight the user's own panning and zooming in between.
 */
function MapFocus({ sites, selectedSite, fallbackCenter }) {
  const map = useMap();
  const signature = `${sites.map((s) => s.key).join('|')}::${selectedSite?.key || 'all'}`;

  // Whether the last framing happened against a properly measured container.
  // fitBounds derives its zoom from the pane size, so framing against a
  // half-measured container silently picks the wrong zoom (a nationwide
  // portfolio computes a huge fit-zoom, gets clamped to maxZoom, and lands you
  // over bare paddock with every pin off-screen). When that happens we retry
  // once the ResizeObserver reports a real size.
  const framedAtSize = useRef(null);

  // Held in a ref so the resize handler can re-apply the current framing
  // without needing to re-subscribe whenever the selection changes.
  const applyRef = useRef(() => {});
  applyRef.current = (animate) => {
    const { x, y } = map.getSize();
    // Too small to derive a zoom from — wait for the resize observer.
    if (x < 80 || y < 80) { framedAtSize.current = null; return; }
    framedAtSize.current = `${x}x${y}`;
    const opts = animate ? { duration: 0.7 } : { animate: false };

    if (!sites.length) {
      map.setView(fallbackCenter, 4, opts);
      return;
    }

    // A single asset is selected → frame that site alone.
    if (selectedSite) {
      map.flyTo([selectedSite.lat, selectedSite.lon], siteZoom(selectedSite.units), opts);
      return;
    }

    // Every asset sits at one site (single-site portfolio, or co-located units
    // like Limondale's two blocks). fitBounds on a zero-area bounds snaps to
    // the max zoom, which lands you inside a single panel row — so treat it as
    // the single-site case instead.
    if (sites.length === 1) {
      map.flyTo([sites[0].lat, sites[0].lon], siteZoom(sites[0].units), opts);
      return;
    }

    // Several distinct sites → fit them all. maxZoom stops a pair of sites a
    // few km apart from zooming past useful context.
    map.fitBounds(
      L.latLngBounds(sites.map((s) => [s.lat, s.lon])),
      { padding: [46, 46], maxZoom: 11, ...opts },
    );
  };

  // Frame on mount and whenever the selection changes.
  //
  // Done synchronously: Leaflet caches its pane size and only remeasures on
  // invalidateSize(), so refreshing that first is what makes the framing
  // correct — deferring the framing itself to requestAnimationFrame would tie
  // it to the tab being actively rendered, and a selection change would
  // silently fail to move the map whenever rAF is throttled. The rAF below is
  // only a retry for the case where the container genuinely hadn't been laid
  // out yet (apply() bails and leaves framedAtSize null).
  useEffect(() => {
    map.invalidateSize();
    applyRef.current(true);

    if (framedAtSize.current !== null) return undefined;
    const raf = requestAnimationFrame(() => {
      map.invalidateSize();
      applyRef.current(false);
    });
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  // Keep Leaflet's size in step with the container (window resize, sidebar
  // collapse, the card appearing after a skeleton). Re-frame only when we
  // never got a valid framing in the first place — otherwise a resize would
  // yank the view back and undo the user's own panning.
  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => {
      map.invalidateSize();
      if (framedAtSize.current === null) applyRef.current(false);
    });
    ro.observe(map.getContainer());
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  return null;
}

export default function PortfolioMap({
  assets, selectedAssetId = 'all', onSelectAsset, loading, colors, period,
  // When true the card stretches to its grid/flex cell and the map pane takes
  // whatever height is left over, so it can sit level with a taller column
  // beside it. Otherwise the pane keeps a fixed height and the card sizes to
  // its content. Either way MIN_PANE_HEIGHT is the floor — Leaflet needs a
  // real height to derive a zoom from.
  fillHeight = false,
}) {
  const [basemap, setBasemap] = useState('satellite');
  const mapRef = useRef(null);

  // Memoised so the derived useMemos below don't see a fresh array identity on
  // every render when the caller passes `data?.assets || []`.
  const rows = useMemo(() => assets || [], [assets]);

  // Split located from unlocated. `lat`/`lon` are null whenever the geo
  // crosswalk has no point for that DUID — a real case (e.g. Gunsynd), not an
  // error state, so we surface it as a coverage note rather than an alert.
  const located = useMemo(
    () => rows.filter((a) => a.lat != null && a.lon != null),
    [rows],
  );
  const unlocated = useMemo(
    () => rows.filter((a) => a.lat == null || a.lon == null),
    [rows],
  );

  // Group units by coordinate → one marker per physical site.
  const sites = useMemo(() => {
    const byKey = new Map();
    for (const a of located) {
      const key = siteKey(a.lat, a.lon);
      if (!byKey.has(key)) {
        byKey.set(key, {
          key,
          lat: a.lat,
          lon: a.lon,
          // The crosswalk's site name ("Limondale Solar Farm") is the right
          // label for a multi-unit pin; fall back to the asset name.
          name: a.site_name || a.name || a.asset_id,
          units: [],
        });
      }
      byKey.get(key).units.push(a);
    }
    // Biggest site last so its marker paints on top of smaller neighbours.
    return [...byKey.values()].sort(
      (x, y) => capOf(x) - capOf(y),
    );
  }, [located]);

  const selectedSite = useMemo(() => {
    if (!selectedAssetId || selectedAssetId === 'all') return null;
    return sites.find((s) => s.units.some((u) => u.asset_id === selectedAssetId)) || null;
  }, [sites, selectedAssetId]);

  const totalLocatedMw = located.reduce((s, a) => s + (a.capacity_mw || 0), 0);

  const bm = BASEMAPS[basemap];
  const tileUrl = typeof bm.url === 'function' ? bm.url(colors.mode) : bm.url;

  return (
    <Card sx={{
      p: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column',
      minWidth: 0,
      ...(fillHeight ? { height: '100%' } : null),
    }}>
      {/* Header — title + coverage count on the left, basemap switch right */}
      <Stack direction="row" justifyContent="space-between" alignItems="center"
             sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Stack direction="row" spacing={1.25} alignItems="baseline" sx={{ minWidth: 0 }}>
          <Typography variant="overline" sx={{ color: 'text.secondary' }}>
            Asset locations
          </Typography>
          {!loading && (
            <Typography variant="caption" sx={{
              color: 'text.disabled', fontVariantNumeric: 'tabular-nums',
            }}>
              · {sites.length} {sites.length === 1 ? 'site' : 'sites'}
              {totalLocatedMw > 0 && ` · ${Math.round(totalLocatedMw)} MW`}
            </Typography>
          )}
        </Stack>

        <Stack direction="row" spacing={0.75} alignItems="center">
          {/* Re-frame button — gives the user a way back after they've panned
              away, without having to reload the page. */}
          {sites.length > 0 && (
            <Tooltip title={selectedSite ? 'Recentre on the selected asset' : 'Fit all assets'}>
              <Button
                size="small"
                onClick={() => {
                  const map = mapRef.current;
                  if (!map) return;
                  if (selectedSite || sites.length === 1) {
                    const s = selectedSite || sites[0];
                    map.flyTo([s.lat, s.lon], siteZoom(s.units), { duration: 0.7 });
                  } else {
                    map.fitBounds(L.latLngBounds(sites.map((s) => [s.lat, s.lon])),
                                  { padding: [46, 46], maxZoom: 11 });
                  }
                }}
                sx={{
                  textTransform: 'none', minWidth: 0, px: 0.85, py: 0.3,
                  fontSize: '0.72rem', color: 'text.secondary',
                  '&:hover': { color: 'primary.main', bgcolor: colors.hover },
                }}
                startIcon={<CenterFocusStrongIcon sx={{ fontSize: 15 }} />}
              >
                Reframe
              </Button>
            </Tooltip>
          )}
          {Object.entries(BASEMAPS).map(([k, cfg]) => (
            <Chip
              key={k}
              label={cfg.label}
              size="small"
              onClick={() => setBasemap(k)}
              variant={basemap === k ? 'filled' : 'outlined'}
              sx={{
                fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer', height: 22,
                bgcolor: basemap === k ? 'rgba(217,138,43,0.14)' : 'transparent',
                borderColor: 'divider',
                color: basemap === k ? 'primary.main' : 'text.secondary',
              }}
            />
          ))}
        </Stack>
      </Stack>

      {/* Map pane. In fillHeight mode this is the flexible element — the
          header and coverage footer keep their natural height and the map
          absorbs the rest of the card. The ResizeObserver in MapFocus keeps
          Leaflet's internal size in step as the row's height settles. */}
      <Box sx={{
        position: 'relative',
        minHeight: MIN_PANE_HEIGHT,
        ...(fillHeight
          ? { flex: '1 1 auto' }
          : { height: 380, flexShrink: 0 }),
      }}>
        {loading ? (
          <Skeleton variant="rectangular" sx={{ position: 'absolute', inset: 0,
                                                bgcolor: colors.skeleton }} />
        ) : sites.length === 0 ? (
          <Box sx={{
            position: 'absolute', inset: 0,
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: 0.75,
            px: 3, textAlign: 'center',
          }}>
            <PlaceOutlinedIcon sx={{ fontSize: 30, color: 'text.disabled' }} />
            <Typography sx={{ color: 'text.secondary', fontSize: '0.88rem' }}>
              No site coordinates on file for this portfolio.
            </Typography>
            <Typography sx={{ color: 'text.disabled', fontSize: '0.76rem', maxWidth: 380 }}>
              Locations come from the Geoscience Australia power-station register
              matched to each DUID. {rows.length > 0
                ? `None of the ${rows.length} ${rows.length === 1 ? 'asset' : 'assets'} here has matched yet.`
                : ''}
            </Typography>
          </Box>
        ) : (
          <MapContainer
            center={AU_CENTER}
            zoom={4}
            minZoom={3}
            scrollWheelZoom={false}   /* page scroll shouldn't zoom the map */
            style={{ height: '100%', width: '100%' }}
            ref={mapRef}
          >
            <TileLayer
              key={basemap + colors.mode}   /* force a swap when either changes */
              url={tileUrl}
              attribution={bm.attribution}
              maxZoom={bm.maxZoom}
              subdomains={basemap === 'street' ? 'abcd' : 'abc'}
            />

            <MapFocus
              sites={sites}
              selectedSite={selectedSite}
              fallbackCenter={AU_CENTER}
            />

            {sites.map((s) => {
              const isSel = selectedSite?.key === s.key;
              // Colour a multi-fuel site by its largest unit's fuel.
              const lead = [...s.units].sort(
                (a, b) => (b.capacity_mw || 0) - (a.capacity_mw || 0),
              )[0];
              return (
                <Marker
                  key={s.key}
                  position={[s.lat, s.lon]}
                  icon={siteIcon(fuelColor(lead?.fuel), capOf(s), isSel, s.units.length)}
                  zIndexOffset={isSel ? 1000 : 0}
                >
                  <Popup minWidth={230}>
                    <SitePopup
                      site={s}
                      period={period}
                      selectedAssetId={selectedAssetId}
                      onSelectAsset={onSelectAsset}
                    />
                  </Popup>
                </Marker>
              );
            })}
          </MapContainer>
        )}
      </Box>

      {/* Coverage footer — names the assets we could NOT place, so a partial
          map is never mistaken for a complete one. */}
      {!loading && unlocated.length > 0 && (
        <Box sx={{
          px: 2, py: 1.1,
          borderTop: '1px solid', borderColor: 'divider',
          display: 'flex', alignItems: 'baseline', gap: 0.75, flexWrap: 'wrap',
        }}>
          <Typography sx={{ fontSize: '0.72rem', color: 'text.disabled' }}>
            {located.length} of {rows.length} located · no coordinates for
          </Typography>
          {unlocated.map((a) => (
            <Tooltip key={a.asset_id} title={`${a.asset_id} · ${a.region || '—'} · ${a.fuel || 'Other'}`}>
              <Box component="span" sx={{
                fontSize: '0.72rem', fontWeight: 600, color: 'text.secondary',
                borderBottom: '1px dotted', borderColor: colors.hairline2,
                cursor: 'default',
              }}>
                {a.name || a.asset_id}
              </Box>
            </Tooltip>
          ))}
        </Box>
      )}
    </Card>
  );
}

// Total nameplate at a site — used for pin sizing and the popup header.
function capOf(site) {
  return site.units.reduce((s, u) => s + (u.capacity_mw || 0), 0);
}

// Popup body. For a co-located site this lists every unit, which is the only
// way to reach the units whose pins are hidden underneath the top one.
function SitePopup({ site, period, selectedAssetId, onSelectAsset }) {
  const multi = site.units.length > 1;
  return (
    <Box sx={{ minWidth: 210 }}>
      <Typography sx={{ fontSize: '0.86rem', fontWeight: 700, lineHeight: 1.25, mb: 0.15 }}>
        {site.name}
      </Typography>
      <Typography sx={{ fontSize: '0.68rem', color: 'text.disabled', mb: 0.9 }}>
        {site.units[0]?.region || '—'} · {Math.round(capOf(site))} MW
        {multi && ` · ${site.units.length} units`}
        {' · '}
        <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {site.lat.toFixed(4)}, {site.lon.toFixed(4)}
        </Box>
      </Typography>

      <Stack spacing={0.6}>
        {site.units.map((u) => {
          const isSel = u.asset_id === selectedAssetId;
          return (
            <Box key={u.asset_id} sx={{
              display: 'flex', alignItems: 'center', gap: 0.75,
              borderTop: '1px solid', borderColor: 'divider', pt: 0.6,
            }}>
              <Box sx={{
                width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
                bgcolor: fuelColor(u.fuel),
              }} />
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography sx={{
                  fontSize: '0.76rem', fontWeight: 600, lineHeight: 1.2,
                  color: isSel ? 'primary.main' : 'text.primary',
                }}>
                  {u.name || u.asset_id}
                </Typography>
                <Typography sx={{ fontSize: '0.66rem', color: 'text.disabled' }}>
                  <Box component="span" sx={{ fontFamily: 'monospace' }}>{u.asset_id}</Box>
                  {' · '}{Math.round(u.capacity_mw || 0)} MW
                  {u.avg_mw != null && ` · ${u.avg_mw.toFixed(1)} MW avg`}
                </Typography>
              </Box>
              {/* Scope the page to this unit — the map doubles as an asset
                  picker, which is the natural gesture once you've found the
                  site you care about. */}
              {onSelectAsset && !isSel && (
                <Button
                  size="small"
                  onClick={() => onSelectAsset(u.asset_id)}
                  sx={{
                    textTransform: 'none', minWidth: 0, px: 0.6, py: 0.1,
                    fontSize: '0.66rem', flexShrink: 0,
                  }}
                >
                  Focus
                </Button>
              )}
            </Box>
          );
        })}
      </Stack>

      {period?.label && (
        <Typography sx={{ fontSize: '0.62rem', color: 'text.disabled', mt: 0.9 }}>
          Output averaged over {period.label}
        </Typography>
      )}
    </Box>
  );
}
